import React from 'react';
import { Box, Button, Stack, TextField, Typography, IconButton, Alert, Chip } from '@material-ui/core';
import { PiCameraBold, PiXCircle } from 'react-icons/pi';
import Sheet from 'react-modal-sheet';
import { useExperience } from '../contexts/experience';
import Layout from './Layout';
import String from './String';
import { useTheme } from '../contexts/theme';
import { QrScanner } from './QrScanner';
import { useSession } from '../contexts/session';
import { useLanguage } from '../contexts/language';
import { extractManualCode, validateManualCodeUrl } from '../utils/manualCode';

function ManualCode() {
  const { playManually, started, isActive } = useExperience();
  const { selectedExperience } = useSession();
  const { styles } = useTheme();
  const { content } = useLanguage();
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState();
  const [qrCodeError, setQrCodeError] = React.useState(null);
  const [openSheet, setOpenSheet] = React.useState(false);

  const suggestions = ['garden', 'wood', 'sea', 'rain'];

  const containerRef = React.useRef(null);
  const codeInputRef = React.useRef(null);
  const qrButtonRef = React.useRef(null);

  const isQrSheetOpen = openSheet && selectedExperience?.qrEnabled;

  const handleCodeChange = (e) => {
    setError();
    setCode(e.target.value);
  };

  const handleCodeFocus = () => {
    setError();
    setCode('');
  };

  const handleConfirm = () => {
    setError();
    const playbackError = playManually(code);
    if (playbackError) setError(playbackError);
    else setCode('');
  };

  const handleSuggestionClick = (suggestion) => {
    setError();
    setCode(suggestion);
    const playbackError = playManually(suggestion);
    if (playbackError) setError(playbackError);
    else setCode('');
  };

  const handleKeyUp = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
      handleConfirm();
    }
  };

  const onScan = React.useCallback(
    (decodedText) => {
      if (!validateManualCodeUrl(decodedText)) {
        const errText = content('qr_invalid');
        setQrCodeError(errText);
        return;
      }

      setQrCodeError(null);
      const decodedAgamiCode = extractManualCode(decodedText);
      setCode(decodedAgamiCode);
      setError();
      setOpenSheet(false);
      const playbackError = playManually(decodedAgamiCode);
      if (playbackError) setError(playbackError);
      else setCode('');
    },
    [content]
  );

  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    if (isQrSheetOpen) {
      node.setAttribute('inert', '');
    } else {
      node.removeAttribute('inert');

      // focus back on button
      const target = qrButtonRef?.current || codeInputRef.current || containerRef.current;
      target?.focus();
    }
    return () => {
      node.removeAttribute('inert');
    };
  }, [isQrSheetOpen]);

  React.useEffect(() => {
    // Delay can help Android
    const id = setTimeout(() => {
      const target = qrButtonRef?.current || codeInputRef.current || containerRef.current;
      target?.focus();
    }, 100);
    return () => clearTimeout(id);
  }, []);

  return (
    <>
      <Sheet isOpen={isQrSheetOpen} onClose={() => setOpenSheet(false)}>
        <Sheet.Container role="dialog" aria-modal="true" aria-label={content('aria_qr_panel')}>
          <Sheet.Header />
          <Sheet.Content>
            <Box position="absolute" top="-30px" right={0}>
              <IconButton
                onClick={() => setOpenSheet(false)}
                aria-label={content('aria_close_qr_scanner')}
                title={content('aria_close_qr_scanner')}
              >
                <PiXCircle />
              </IconButton>
            </Box>
            <QrScanner onScan={onScan} />
            <Alert
              role="alert"
              severity="error"
              style={{
                visibility: qrCodeError ? 'visible' : 'hidden',
              }}
            >
              {qrCodeError}
            </Alert>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>

      <Box ref={containerRef} aria-hidden={isQrSheetOpen} flexGrow={1} tabIndex={-1}>
        <Layout bgColor={styles.palette.background.default}>
          <Stack pt={3} spacing={3} style={{ marginInline: 50 }}>
            <Box textAlign="center">
              <Typography variant="subtitle">
                <String s="insert_code" />
              </Typography>
            </Box>

            <Box textAlign="center">
              <TextField
                aria-label={content('aria_manual_playback_code_input')}
                color="secondary"
                variant="standard"
                onChange={handleCodeChange}
                onFocus={handleCodeFocus}
                onKeyUp={handleKeyUp}
                value={code}
                inputRef={codeInputRef}
                inputProps={{
                  type: 'text',
                  style: { fontSize: 28, textAlign: 'center', width: '200px' },
                  autoCapitalize: 'none',
                }}
                disabled={!started || isActive}
                error={!!error}
                data-testid="manual-code-input"
              />
              <Box py={1} aria-live="assertive" aria-hidden={!error}>
                {error ? (
                  <Typography color="red" fontWeight={600}>
                    {error}
                  </Typography>
                ) : (
                  <Typography style={{ opacity: 0 }}>No error</Typography>
                )}
              </Box>
            </Box>

            <Box textAlign="center">
              <Button
                disabled={code.length === 0}
                aria-disabled={code.length === 0}
                color="primary"
                variant="contained"
                onClick={handleConfirm}
                aria-label={content('aria_start_manual_content')}
                data-testid="manual-code-play-btn"
              >
                <String s="play" />
              </Button>
            </Box>

            <Box textAlign="center" pt={2}>
              <Typography variant="body2" style={{ marginBottom: 12 }}>
                <String s="try_with" />
              </Typography>
              <Box display="flex" flexWrap="wrap" justifyContent="center" gap={1} style={{ gap: 8 }}>
                {suggestions.map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={!started || isActive}
                    clickable
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Stack>

          {selectedExperience?.qrEnabled && (
            <Box textAlign="center">
              <Button
                variant="contained"
                startIcon={<PiCameraBold />}
                onClick={() => {
                  setQrCodeError(null);
                  setOpenSheet(true);
                }}
                ref={qrButtonRef}
                aria-label={content('aria_open_qr_scanner')}
              >
                <String s="qr_scan" />
              </Button>
            </Box>
          )}
        </Layout>
      </Box>
    </>
  );
}

export default ManualCode;
